import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import DataTable from '../../../components/ui/DataTable';
import Button from '../../../components/ui/Button';
import LoadErrorBanner from '../../../components/ui/LoadErrorBanner';
import InviteToPostingDrawer from './InviteToPostingDrawer';
import InviteResultPanel from './InviteResultPanel';
import { selectionColumn } from './SelectionBar';
import { fetchPoolSuggestions } from '../config/poolApi';

// HAVUZ ÖNERİLERİ — kadro talebi için havuzlardaki uygun adaylar
// (`GET v1/ats-requisitions/{id}/pool-suggestions`, AT.Write). Sunucu yalnız rızası
// geçerli, anonimleştirilmemiş ve bu kadroya aktif başvurusu OLMAYAN kişileri döner;
// en fazla 25, skor + eşleşme nedenleri (`title`, `keywords`, `location`, `experience`).
//
// Seçilenler "Pozisyona Davet Et" ile kadronun YAYINDAKİ ilanına davet edilir — ilan
// seçici `requisitionId` ile o kadronun ilanlarına daraltılır.
//
// Bu bileşen kendi başına durur: kadro çekmecesine ana oturum bağlar.

const ReasonWrap = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const ReasonChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.primary.main};
  background: rgba(2, 145, 239, 0.1);
  white-space: nowrap;
`;

const ScoreBadge = styled.span`
  display: inline-flex;
  min-width: 36px;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.bgColor};
`;

export const SUGGESTION_REASONS = ['title', 'keywords', 'location', 'experience'];

export const formatScore = (score) => {
  if (score === null || score === undefined || score === '') return '-';
  const n = Number(score);
  if (!Number.isFinite(n)) return '-';
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

// Öneri satırının aday kimliği — davet gövdesine giden `candidateIds` bundan türer.
const candidateIdOf = (row) => row.candidateId || row.id;

export default function PoolSuggestionsSection({ requisitionId }) {
  const { t } = useTranslation();
  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!requisitionId) return undefined;
    let alive = true;
    setFetching(true);
    fetchPoolSuggestions(requisitionId)
      .then(({ ok, items }) => {
        if (!alive) return;
        setLoadFailed(!ok);
        setRows(items);
        setSelectedIds([]);
      })
      .finally(() => { if (alive) setFetching(false); });
    return () => { alive = false; };
  }, [requisitionId, reloadKey]);

  const toggle = useCallback((id) => {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }, []);

  const reasonLabel = useCallback(
    (code) => t(`ats.candidatePool.reason.${code}`, { defaultValue: code }),
    [t],
  );

  // Sıralama KAPALI: sunucu skora göre sıralı döner; istemci sıralaması satır
  // indeksini kaydırır ve seçim kolonunun `data[rowIndex]` eşlemesini bozardı.
  const columns = useMemo(() => [
    selectionColumn({ data: rows, selectedIds, onToggle: toggle, idOf: candidateIdOf }),
    { name: 'fullName', label: t('ats.candidate.fullName'), options: { filter: false, sort: false } },
    {
      name: 'currentTitle',
      label: t('ats.candidate.currentTitle'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'location',
      label: t('ats.candidate.location'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'experienceYears',
      label: t('ats.candidate.experienceYears'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ?? '-') },
    },
    {
      name: 'poolName',
      label: t('ats.candidatePool.pool'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'score',
      label: t('ats.candidatePool.score'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => <ScoreBadge data-role="pool-suggestion-score">{formatScore(v)}</ScoreBadge>,
      },
    },
    {
      name: 'reasons',
      label: t('ats.candidatePool.reasons'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) => {
          const list = Array.isArray(v) ? v : [];
          if (list.length === 0) return '-';
          return (
            <ReasonWrap>
              {list.map((code) => (
                <ReasonChip key={code} data-role={`pool-suggestion-reason-${code}`}>{reasonLabel(code)}</ReasonChip>
              ))}
            </ReasonWrap>
          );
        },
      },
    },
  ], [t, rows, selectedIds, toggle, reasonLabel]);

  const toolbarActions = (
    <Button
      type="button"
      data-role="pool-suggestions-invite"
      disabled={selectedIds.length === 0}
      onClick={() => setInviteOpen(true)}
    >
      {t('ats.candidatePool.inviteToPositionCount', { count: selectedIds.length })}
    </Button>
  );

  // Adlar YENİLEMEDEN ÖNCEKİ satırlardan sabitlenir — liste yeniden yüklenince
  // atlanan adayın satırı değişse de özet doğru adı gösterir.
  const handleResult = useCallback((result) => {
    const names = rows.reduce((acc, r) => ({ ...acc, [candidateIdOf(r)]: r.fullName }), {});
    setInviteResult({ ...result, names });
    setReloadKey((k) => k + 1);
  }, [rows]);

  return (
    <div data-role="pool-suggestions">
      {loadFailed && <LoadErrorBanner message={t('ats.candidatePool.suggestionsLoadFailed')} />}

      <InviteResultPanel
        result={inviteResult}
        nameOf={(id) => inviteResult?.names?.[id]}
        onDismiss={() => setInviteResult(null)}
      />

      <InviteToPostingDrawer
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        candidateIds={selectedIds}
        requisitionId={requisitionId}
        onResult={handleResult}
      />

      <DataTable
        title={t('ats.candidatePool.suggestionsTitle')}
        fetching={fetching}
        columns={columns}
        data={rows}
        totalRowCount={rows.length}
        serverSide={false}
        disableFilters
        tableBodyMaxHeight="none"
        toolbarActions={toolbarActions}
      />
    </div>
  );
}
